/**
 * Modal Manager Types
 *
 * Centralized modal coordination system for stack-based (LIFO) modal management,
 * navigation queuing, and loading gate synchronization.
 */

import { DeepLinkEntity } from '../utils/deep-linking';

/**
 * Unique identifiers for all modals in the application.
 * Add new modal IDs here when creating new modals.
 */
export enum ModalId {
  CARD_EDIT = 'card-edit',
  NOTIFICATIONS = 'notifications',
  PROFILE = 'profile',
  PROFILE_PICTURE_SELECTOR = 'profile-picture-selector',
  BOARD_SELECTOR = 'board-selector',
  BOARD_SETTINGS = 'board-settings',
  TEMPLATE_SELECTOR = 'template-selector',
  USER_DETAILS = 'user-details',
  ORG_DETAILS = 'org-details',
}

/**
 * Priority levels for modal display.
 * Higher priority modals can interrupt lower priority ones.
 */
export enum ModalPriority {
  LOW = 0,
  NORMAL = 1,
  HIGH = 2,
  CRITICAL = 3,
}

/**
 * Queued navigation action to be executed after modals close
 * Uses DeepLinkEntity for type consistency
 */
export interface NavigationAction {
  type: DeepLinkEntity;
  id: string;
  params?: Record<string, unknown>;
}

/**
 * Internal modal stack item
 */
export interface ModalStackItem {
  id: ModalId;
  priority: ModalPriority;
  openedAt: number;
  zIndex: number;
}

/**
 * Loading gate registration
 */
export interface LoadingGate {
  id: string;
  isLoading: boolean;
  resolvers: Array<() => void>;
  timeout?: NodeJS.Timeout;
}

/**
 * Modal Manager Context Value
 */
export interface ModalManagerContextValue {
  /**
   * Open a modal with optional priority
   */
  openModal: (id: ModalId, priority?: ModalPriority) => void;

  /**
   * Close a specific modal
   */
  closeModal: (id: ModalId) => void;

  /**
   * Close all modals
   */
  closeAll: () => void;

  /**
   * Check if a specific modal is open
   */
  isModalOpen: (id: ModalId) => boolean;

  /**
   * Get the z-index for a modal (based on stack position)
   */
  getZIndex: (id: ModalId) => number;

  /**
   * Get the current modal stack
   */
  getStack: () => ModalStackItem[];

  /**
   * Queue a navigation action to be executed after modals close
   */
  queueNavigation: (action: NavigationAction) => void;

  /**
   * Register a loading gate
   * @param gateId - Unique identifier for the gate
   * @param isLoading - Current loading state
   */
  registerLoadingGate: (gateId: string, isLoading: boolean) => void;

  /**
   * Wait for a loading gate to open (isLoading becomes false)
   * @param gateId - The gate to wait for
   * @param timeoutMs - Optional timeout in milliseconds (default: 5000)
   * @returns Promise that resolves when gate opens or times out
   */
  waitForGate: (gateId: string, timeoutMs?: number) => Promise<void>;
}
